import { Component } from '@theme/component';

/**
 * Subcollection Links Component
 * Handles drag-to-scroll functionality for horizontal link navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} container - The scrollable container element.
 *
 * @extends {Component<Refs>}
 */
class SubcollectionLinksComponent extends Component {
  requiredRefs = ['container'];

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} */
  #startX = 0;

  /** @type {number} */
  #scrollLeft = 0;

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const { container } = this.refs;

    if (!container) return;

    // Mouse events for drag-to-scroll (use capture to handle events on links)
    container.addEventListener('mousedown', this.#handleMouseDown, { signal, capture: true });
    container.addEventListener('mousemove', this.#handleMouseMove, { signal });
    container.addEventListener('mouseup', this.#handleMouseUp, { signal });
    container.addEventListener('mouseleave', this.#handleMouseLeave, { signal });

    // Touch events for mobile drag (use capture for touchstart)
    container.addEventListener('touchstart', this.#handleTouchStart, { signal, passive: true, capture: true });
    container.addEventListener('touchmove', this.#handleTouchMove, { signal, passive: false });
    container.addEventListener('touchend', this.#handleTouchEnd, { signal });

    // Prevent click events during drag
    container.addEventListener('click', this.#handleClick, { signal, capture: true });

    // Listen for scroll events to hide/show gradient
    container.addEventListener('scroll', this.#handleScroll, { signal });

    // Listen for resize to update gradient when viewport changes
    window.addEventListener('resize', this.#handleResize, { signal });

    // Initial check for gradient visibility
    this.#updateGradientVisibility();
  }

  /**
   * Handles resize event to update gradient visibility.
   */
  #handleResize = () => {
    this.#updateGradientVisibility();
  };

  /**
   * Handles scroll event to update gradient visibility.
   */
  #handleScroll = () => {
    this.#updateGradientVisibility();
  };

  /**
   * Updates the gradient visibility based on scroll position.
   */
  #updateGradientVisibility = () => {
    const { container } = this.refs;
    const isScrollable = container.scrollWidth > container.clientWidth;
    const isScrolledToEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 5;

    // Hide gradient if not scrollable or scrolled to end
    if (!isScrollable || isScrolledToEnd) {
      this.classList.add('subcollection-links--scrolled-end');
    } else {
      this.classList.remove('subcollection-links--scrolled-end');
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles mousedown event to start dragging.
   * @param {MouseEvent} event - The mouse event.
   */
  #handleMouseDown = (event) => {
    // Only handle left mouse button
    if (event.button !== 0) return;

    // Prevent default to stop link drag behavior
    event.preventDefault();

    this.#isDragging = true;
    this.#startX = event.pageX - this.refs.container.offsetLeft;
    this.#scrollLeft = this.refs.container.scrollLeft;
  };

  /**
   * Handles mousemove event during dragging.
   * @param {MouseEvent} event - The mouse event.
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;

    event.preventDefault();
    const x = event.pageX - this.refs.container.offsetLeft;
    const walk = (x - this.#startX) * 1.5; // Scroll speed multiplier
    this.refs.container.scrollLeft = this.#scrollLeft - walk;
  };

  /**
   * Handles mouseup event to stop dragging.
   */
  #handleMouseUp = () => {
    this.#isDragging = false;
  };

  /**
   * Handles mouseleave event to stop dragging.
   */
  #handleMouseLeave = () => {
    this.#isDragging = false;
  };

  /**
   * Handles touchstart event for mobile.
   * @param {TouchEvent} event - The touch event.
   */
  #handleTouchStart = (event) => {
    if (!event.touches[0]) return;

    this.#isDragging = true;
    this.#startX = event.touches[0].pageX - this.refs.container.offsetLeft;
    this.#scrollLeft = this.refs.container.scrollLeft;
  };

  /**
   * Handles touchmove event for mobile.
   * @param {TouchEvent} event - The touch event.
   */
  #handleTouchMove = (event) => {
    if (!this.#isDragging || !event.touches[0]) return;

    const x = event.touches[0].pageX - this.refs.container.offsetLeft;
    const walk = (x - this.#startX) * 1.5;
    this.refs.container.scrollLeft = this.#scrollLeft - walk;
  };

  /**
   * Handles touchend event to stop dragging.
   */
  #handleTouchEnd = () => {
    this.#isDragging = false;
  };

  /**
   * Prevents click events when dragging.
   * @param {MouseEvent} event - The click event.
   */
  #handleClick = (event) => {
    if (Math.abs(this.refs.container.scrollLeft - this.#scrollLeft) > 5) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
}

customElements.define('subcollection-links-component', SubcollectionLinksComponent);
